import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  useDeleteWorkspace,
  useUpdateWorkspace,
} from '../../hooks/useWorkspaces'
import { useAuthStore } from '../../store/auth.store'
import { Workspace, WorkspaceRole } from '../../types/models'
import { Button } from '../common/Button'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { Input } from '../common/Input'
import { Modal } from '../common/Modal'
import { WorkspaceMemberManager } from './WorkspaceMemberManager'
import './WorkspaceSettings.css'

export interface WorkspaceSettingsProps {
  workspace: Workspace
  isOpen: boolean
  onClose: () => void
}

/**
 * WorkspaceSettings Component
 * Modal for managing workspace settings
 * Implements Requirements 2.3, 2.4
 */
export const WorkspaceSettings: React.FC<WorkspaceSettingsProps> = ({
  workspace,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState<'general' | 'members'>('general')
  const [name, setName] = useState(workspace.name)
  const [description, setDescription] = useState(workspace.description || '')
  const [errors, setErrors] = useState<{ name?: string; description?: string }>(
    {}
  )
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const updateWorkspace = useUpdateWorkspace()
  const deleteWorkspace = useDeleteWorkspace()

  // Get current user's role
  const currentUserMember = workspace.members.find((m) => m.userId === user?.id)
  const userRole = currentUserMember?.role
  const canEdit =
    userRole === WorkspaceRole.OWNER || userRole === WorkspaceRole.ADMIN
  const canDelete = userRole === WorkspaceRole.OWNER

  useEffect(() => {
    setName(workspace.name)
    setDescription(workspace.description || '')
  }, [workspace])

  const validateForm = (): boolean => {
    const newErrors: { name?: string; description?: string } = {}

    if (!name.trim()) {
      newErrors.name = t('validation.workspaceNameRequired')
    } else if (name.length > 100) {
      newErrors.name = t('validation.workspaceNameTooLong')
    }

    if (description && description.length > 500) {
      newErrors.description = t('validation.descriptionTooLong')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm() || !canEdit) {
      return
    }

    try {
      await updateWorkspace.mutateAsync({
        id: workspace.id,
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
        },
      })
    } catch (error) {
      // Error is handled by the mutation hook
    }
  }

  const handleDelete = async () => {
    try {
      await deleteWorkspace.mutateAsync(workspace.id)
      setIsDeleteDialogOpen(false)
      onClose()
      navigate('/workspaces')
    } catch (error) {
      // Error is handled by the mutation hook
    }
  }

  const handleClose = () => {
    if (!updateWorkspace.isPending && !deleteWorkspace.isPending) {
      setName(workspace.name)
      setDescription(workspace.description || '')
      setErrors({})
      setActiveTab('general')
      onClose()
    }
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('workspace.settings')}
        size="lg"
      >
        <div className="workspace-settings">
          <div className="workspace-settings__tabs">
            <button
              className={`workspace-settings__tab ${activeTab === 'general' ? 'workspace-settings__tab--active' : ''}`}
              onClick={() => setActiveTab('general')}
            >
              {t('common.general')}
            </button>
            <button
              className={`workspace-settings__tab ${activeTab === 'members' ? 'workspace-settings__tab--active' : ''}`}
              onClick={() => setActiveTab('members')}
            >
              {t('common.members')}
            </button>
          </div>

          <div className="workspace-settings__content">
            {activeTab === 'general' && (
              <div className="workspace-settings__general">
                <Input
                  label={t('workspace.name')}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={errors.name}
                  placeholder={t('workspace.enterName')}
                  disabled={!canEdit}
                />

                <Input
                  label={t('workspace.descriptionOptional')}
                  type="textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  error={errors.description}
                  placeholder={t('workspace.enterDescription')}
                  rows={4}
                  disabled={!canEdit}
                />

                {canEdit && (
                  <div className="workspace-settings__actions">
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      loading={updateWorkspace.isPending}
                    >
                      {t('workspace.saveChanges')}
                    </Button>
                  </div>
                )}

                {canDelete && (
                  <div className="workspace-settings__danger-zone">
                    <h3>{t('workspace.dangerZone')}</h3>
                    <p>{t('workspace.deleteWarning')}</p>
                    <Button
                      variant="danger"
                      onClick={() => setIsDeleteDialogOpen(true)}
                    >
                      {t('workspace.deleteWorkspace')}
                    </Button>
                  </div>
                )}

                {!canEdit && (
                  <p className="workspace-settings__no-permission">
                    {t('workspace.noPermissionEdit')}
                  </p>
                )}
              </div>
            )}

            {activeTab === 'members' && (
              <WorkspaceMemberManager
                workspaceId={workspace.id}
                members={workspace.members}
                currentUserRole={userRole}
              />
            )}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title={t('workspace.deleteWorkspace')}
        message={t('workspace.deleteWorkspaceConfirm', {
          name: workspace.name,
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setIsDeleteDialogOpen(false)}
        loading={deleteWorkspace.isPending}
      />
    </>
  )
}
